import React, { useState, useEffect } from 'react';
import { TrendingDown, Users, DollarSign, Activity, Timer, UserCheck, Sparkles, Search, X, ChevronLeft, ChevronRight, BarChart3, Inbox, AlertTriangle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { useTheme } from '../../../context/ThemeContext';
import { apiGet } from '../../../utils/api';
import AskJPro from '../AskJPro';
import SmartFilter from '../SmartFilter';
import toast from 'react-hot-toast';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PAGE_SIZE = 25;
// Manual chips, ordered most-urgent first (matches the table sort).
const LIFECYCLE_CHIPS = ['lapsed', 'dormant', 'lapsing', 'active', 'never'];

const LIFECYCLE_META = {
  active:  { label: 'Active', bg: 'bg-green-100 text-green-700', dark: 'bg-green-900/40 text-green-300', order: 4 },
  lapsing: { label: 'Lapsing', bg: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/40 text-orange-300', order: 2 },
  lapsed:  { label: 'Lapsed', bg: 'bg-red-100 text-red-700', dark: 'bg-red-900/40 text-red-300', order: 0 },
  dormant: { label: 'Dormant', bg: 'bg-red-100 text-red-800', dark: 'bg-red-900/50 text-red-200', order: 1 },
  never:   { label: 'Never audited', bg: 'bg-gray-100 text-gray-600', dark: 'bg-gray-700 text-gray-300', order: 3 },
};

const money = (n) => (n ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—');

const fmtDate = (s) => {
  if (!s) return null;
  const dt = new Date(`${s}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? s : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysAgo = (s) => {
  if (!s) return null;
  const dt = new Date(`${s}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : Math.floor((Date.now() - dt.getTime()) / 86400000);
};

const DirectorDashboardTab = () => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAsk, setShowAsk] = useState(false);
  const [filters, setFilters] = useState({});      // AI smart-filter
  const [search, setSearch] = useState('');         // manual name/state search
  const [lifecycleChip, setLifecycleChip] = useState(''); // manual lifecycle filter
  const [page, setPage] = useState(1);
  const year = new Date().getFullYear();

  // Reset to page 1 whenever the effective filter set changes.
  useEffect(() => { setPage(1); }, [filters, search, lifecycleChip]);

  useEffect(() => {
    setLoading(true);
    apiGet(`/admin/audit_tracker/director_dashboard?year=${year}`)
      .then(async (r) => { if (r.ok) setData(await r.json()); else toast.error('Failed to load director dashboard'); })
      .catch(() => toast.error('Failed to load director dashboard'))
      .finally(() => setLoading(false));
  }, [year]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;
  if (!data) return null;

  const { summary, throughput, turnaround, review_backlog: backlog, sla, outcomes, rework, capacity, work_composition: composition, team_workload: teamWorkload, engagement_available: engagementAvailable, client_lifecycle } = data;
  const lc = summary.lifecycle_counts || {};
  const card = `rounded-xl border ${d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`;
  const muted = d ? 'text-gray-400' : 'text-gray-500';
  const th = `text-left text-[11px] font-bold uppercase tracking-widest font-rasa px-4 py-2 ${muted}`;
  const td = `px-4 py-3 text-[13px] font-rasa ${d ? 'text-gray-200' : 'text-gray-700'}`;

  // Filter the client table client-side (all rows are loaded). AI smart-filter
  // (`filters`) and the manual search/chip controls are AND-combined.
  const matchesFilters = (c) => {
    // AI filters
    if (filters.search && !c.name?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.lifecycle && c.lifecycle !== filters.lifecycle) return false;
    if (filters.us_state && c.state !== filters.us_state) return false;
    if (filters.min_contract && Number(c.contract_price || 0) < Number(filters.min_contract)) return false;
    if (filters.never_logged_in && c.last_contact_login) return false;
    if (filters.login_stale_days) {
      const ago = daysAgo(c.last_contact_login);
      if (ago !== null && ago < Number(filters.login_stale_days)) return false;
    }
    // Manual controls
    if (lifecycleChip && c.lifecycle !== lifecycleChip) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(`${c.name || ''} ${c.state || ''}`.toLowerCase().includes(q))) return false;
    }
    return true;
  };

  const sorted = [...(client_lifecycle || [])].filter(matchesFilters).sort(
    (a, b) => (LIFECYCLE_META[a.lifecycle]?.order ?? 9) - (LIFECYCLE_META[b.lifecycle]?.order ?? 9) || (a.name || '').localeCompare(b.name || '')
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const colCount = engagementAvailable ? 7 : 6;

  // Chart data — turnaround by completion month (null = no completions that month)
  // and workload stacked bars per reviewer (avg cycle carried for the tooltip).
  const turnaroundMonthly = MONTHS.map((name, i) => {
    const m = (turnaround?.monthly || []).find((x) => x.month === i + 1);
    return { name, avg_cycle_days: m?.avg_cycle_days ?? null, completed: m?.completed ?? 0 };
  });
  const hasTurnaroundTrend = (turnaround?.monthly || []).length > 0;
  const workloadData = (teamWorkload || []).map((w) => ({
    name: w.name || `Reviewer #${w.user_id}`,
    in_flight: w.in_flight,
    completed: w.completed,
    avg: w.avg_cycle_days,
  }));
  const chartTooltipStyle = { background: d ? '#1f2937' : '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontFamily: 'Rasa' };
  const axisTick = { fontSize: 11, fill: d ? '#6b7280' : '#9ca3af', fontFamily: 'Rasa' };

  const capacityData = (capacity || []).map((m) => ({ name: MONTHS[m.month - 1], opened: m.opened, approved: m.approved }));
  const hasCapacity = (capacity || []).some((m) => m.opened || m.approved);
  const certTypes = Object.entries(composition?.cert_types || {}).sort((a, b) => b[1] - a[1]);
  const auditTypes = Object.entries(composition?.audit_types || {}).sort((a, b) => b[1] - a[1]);
  const hasComposition = certTypes.length > 0 || auditTypes.length > 0;
  const compMax = Math.max(1, ...certTypes.map((e) => e[1]), ...auditTypes.map((e) => e[1]));

  return (
    <div className="space-y-5">
      {/* Director briefing — AI over the same payload this tab renders */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <SmartFilter context="director" onApplyFilters={setFilters} />
        </div>
        <button
          onClick={() => setShowAsk((v) => !v)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-rasa font-medium shrink-0 ${
            showAsk
              ? 'bg-violet-600 text-white'
              : 'bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700'
          }`}
        >
          <Sparkles size={14} /> {showAsk ? 'Hide briefing' : 'Ask JPro'}
        </button>
      </div>
      {showAsk && <AskJPro mode="director" portfolioData={data} onClose={() => setShowAsk(false)} />}
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className={`${card} p-5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}><Users size={13} /> Clients</div>
          <div className="text-2xl font-dm-serif">{summary.total_clients}</div>
          <div className={`text-[12px] font-rasa mt-1 ${muted}`}>{lc.active || 0} active this year</div>
        </div>
        <div className={`${card} p-5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}><TrendingDown size={13} /> At risk</div>
          <div className="text-2xl font-dm-serif text-red-500">{(lc.lapsed || 0) + (lc.dormant || 0)}</div>
          <div className={`text-[12px] font-rasa mt-1 ${muted}`}>{lc.lapsing || 0} lapsing · {lc.never || 0} never</div>
        </div>
        <div className={`${card} p-5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}><DollarSign size={13} /> Contract value at risk</div>
          <div className="text-2xl font-dm-serif text-orange-500">{money(summary.at_risk_contract_value)}</div>
          <div className={`text-[12px] font-rasa mt-1 ${muted}`}>of {money(summary.total_contract_value)} total</div>
        </div>
        <div className={`${card} p-5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}><Activity size={13} /> {throughput.year} files</div>
          <div className="text-2xl font-dm-serif">{throughput.in_flight?.toLocaleString?.() ?? throughput.in_flight}<span className={`text-sm font-rasa ${muted}`}> in flight</span></div>
          <div className={`text-[12px] font-rasa mt-1 ${muted}`}>{throughput.completed} approved · {throughput.received != null ? `${throughput.received} received · ` : ''}{throughput.total_files?.toLocaleString?.()} opened</div>
        </div>
        <div className={`${card} p-5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}><Timer size={13} /> Turnaround</div>
          <div className="text-2xl font-dm-serif">
            {turnaround?.avg_cycle_days != null ? <>{turnaround.avg_cycle_days}<span className={`text-sm font-rasa ${muted}`}> days avg</span></> : '—'}
          </div>
          <div className={`text-[12px] font-rasa mt-1 ${muted}`}>
            {turnaround?.measured_files
              ? `${turnaround.median_cycle_days}d median · ${turnaround.measured_files} files${turnaround.basis === 'received_to_approved' ? ' · review time' : ''}`
              : 'no completed files yet'}
          </div>
        </div>
        <div className={`${card} p-5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}><Inbox size={13} /> Review backlog</div>
          <div className="text-2xl font-dm-serif">{backlog?.files != null ? backlog.files.toLocaleString() : '—'}</div>
          <div className={`text-[12px] font-rasa mt-1 ${muted}`}>
            {backlog?.files == null
              ? 'backlog unavailable'
              : (backlog.files
                ? <>awaiting approval{backlog.buckets?.over_90 ? <> · <span className="text-orange-500 font-medium">{backlog.buckets.over_90.toLocaleString()} over 90d</span></> : ''}</>
                : 'nothing waiting')}
          </div>
        </div>
      </div>

      {/* SLA pressure strip — only when there's something to act on */}
      {sla?.available && (sla.overdue > 0 || sla.due_soon > 0 || sla.rush > 0 || sla.priority > 0) && (
        <div className={`flex items-center gap-2 rounded-xl p-3 border ${d ? 'bg-orange-900/15 border-orange-800/40 text-orange-200' : 'bg-orange-50 border-orange-200 text-orange-800'}`}>
          <AlertTriangle size={16} className="text-orange-500 shrink-0" />
          <span className="text-[13px] font-rasa">
            {[
              sla.overdue > 0 && <strong key="o">{sla.overdue.toLocaleString()} overdue</strong>,
              sla.due_soon > 0 && <span key="d">{sla.due_soon.toLocaleString()} due within 7 days</span>,
              sla.rush > 0 && <span key="r">{sla.rush.toLocaleString()} rush</span>,
              sla.priority > 0 && <span key="p">{sla.priority.toLocaleString()} priority</span>,
            ].filter(Boolean).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' · ', el]), [])}
            <span className={`ml-1 ${muted}`}>(open files needing attention)</span>
          </span>
        </div>
      )}

      {/* Outcomes for the year's files */}
      {outcomes?.available && outcomes.total > 0 && (
        <div className={`${card} p-4`}>
          <div className={`flex items-center justify-between mb-2`}>
            <span className={`text-[11px] font-bold uppercase tracking-widest font-rasa ${muted}`}>{throughput.year} outcomes</span>
            <span className={`text-[12px] font-rasa ${muted}`}>{outcomes.total.toLocaleString()} files opened</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] font-rasa">
            {[
              { label: 'Approved', value: outcomes.approved, color: 'text-emerald-500' },
              { label: 'Open', value: outcomes.open, color: '' },
              { label: 'Denied', value: outcomes.denied, color: 'text-red-500' },
              { label: 'Canceled', value: outcomes.canceled, color: 'text-gray-400' },
            ].map((o) => (
              <span key={o.label} className={d ? 'text-gray-300' : 'text-gray-700'}>
                <span className={`font-dm-serif text-base ${o.color}`}>{o.value.toLocaleString()}</span> {o.label}
                <span className={`ml-1 ${muted}`}>({Math.round((o.value / outcomes.total) * 100)}%)</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Turnaround trend + team workload charts (auto-hide when the mirror has
          no completed-file / reviewer data; an explicit note replaces them so
          they don't silently vanish). */}
      {!hasTurnaroundTrend && workloadData.length === 0 ? (
        <div className={`${card} p-6 flex items-start gap-3`}>
          <BarChart3 size={18} className={`mt-0.5 shrink-0 ${muted}`} />
          <div>
            <div className={`text-[13px] font-rasa font-medium ${d ? 'text-gray-300' : 'text-gray-700'}`}>
              Turnaround &amp; workload charts appear once files reach final approval this year.
            </div>
            <div className={`text-[12px] font-rasa mt-1 ${muted}`}>
              The mirror shows {throughput.total_files?.toLocaleString?.() || throughput.total_files} files opened for {throughput.year}{throughput.completed ? `, ${throughput.completed} approved` : ' with none approved yet'}. Turnaround is measured from document receipt to final approval; reviewer workload needs the widened file-audit view synced.
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {hasTurnaroundTrend && (
            <div className={`${card} p-5 ${workloadData.length === 0 ? 'lg:col-span-2' : ''}`}>
              <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-4 ${muted}`}>
                <Timer size={13} /> Turnaround trend — avg cycle days by completion month
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={turnaroundMonthly} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="d" />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    formatter={(value, _name, { payload }) => [`${value}d avg · ${payload.completed} completed`, 'Cycle time']}
                  />
                  <Bar dataKey="avg_cycle_days" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {workloadData.length > 0 && (
            <div className={`${card} p-5 ${!hasTurnaroundTrend ? 'lg:col-span-2' : ''}`}>
              <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-4 ${muted}`}>
                <UserCheck size={13} /> Team workload — {throughput.year} files by reviewer
              </div>
              <ResponsiveContainer width="100%" height={Math.max(160, workloadData.length * 40)}>
                <BarChart data={workloadData} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 0 }}>
                  <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ ...axisTick, fontSize: 12 }} axisLine={false} tickLine={false} width={130} />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    labelFormatter={(label, payload) => {
                      const avg = payload?.[0]?.payload?.avg;
                      return avg != null ? `${label} — ${avg}d avg cycle` : label;
                    }}
                  />
                  <Bar dataKey="in_flight" stackId="a" fill="#f97316" name="In flight" />
                  <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-5 mt-2">
                {[{ color: '#f97316', label: 'In flight' }, { color: '#10b981', label: 'Completed' }].map((l) => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} />
                    <span className={`text-[11px] font-rasa ${muted}`}>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rework / quality — correction volume + worst-offender clients */}
      {rework?.available && (rework.correction > 0 || rework.initial > 0) && (
        <div className={`${card} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-[11px] font-bold uppercase tracking-widest font-rasa ${muted}`}>{rework.year} rework</span>
            <span className={`text-[13px] font-rasa ${d ? 'text-gray-200' : 'text-gray-700'}`}>
              <span className={`font-dm-serif text-base ${rework.rework_rate > 50 ? 'text-orange-500' : ''}`}>{rework.rework_rate ?? '—'}%</span> rework rate
              <span className={`ml-1 ${muted}`}>({rework.correction.toLocaleString()} corrections / {rework.initial.toLocaleString()} initial reviews)</span>
            </span>
          </div>
          {rework.top_clients?.length > 0 && (
            <div className="mt-1">
              <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-1 ${muted}`}>Most corrections</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] font-rasa">
                {rework.top_clients.map((tc) => (
                  <span key={tc.company_id} className={d ? 'text-gray-300' : 'text-gray-700'}>
                    {tc.name || `Company #${tc.company_id}`} <span className="text-orange-500 font-medium">{tc.corrections.toLocaleString()}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Capacity net-flow + work composition */}
      {(hasCapacity || hasComposition) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {hasCapacity && (
            <div className={`${card} p-5 ${hasComposition ? '' : 'lg:col-span-2'}`}>
              <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-4 ${muted}`}>
                <Activity size={13} /> Capacity — opened vs approved by month
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={capacityData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Bar dataKey="opened" fill="#3b82f6" name="Opened" radius={[3, 3, 0, 0]} maxBarSize={18} />
                  <Bar dataKey="approved" fill="#10b981" name="Approved" radius={[3, 3, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-5 mt-2">
                {[{ c: '#3b82f6', l: 'Opened' }, { c: '#10b981', l: 'Approved' }].map((x) => (
                  <div key={x.l} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: x.c }} />
                    <span className={`text-[11px] font-rasa ${muted}`}>{x.l}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasComposition && (
            <div className={`${card} p-5 ${hasCapacity ? '' : 'lg:col-span-2'}`}>
              <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest font-rasa mb-4 ${muted}`}>
                <BarChart3 size={13} /> Work composition — {throughput.year}
              </div>
              <div className="grid grid-cols-2 gap-5">
                {[{ title: 'Cert type', rows: certTypes }, { title: 'Audit type', rows: auditTypes }].map((grp) => (
                  <div key={grp.title}>
                    <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-2 ${muted}`}>{grp.title}</div>
                    <div className="space-y-1.5">
                      {grp.rows.length === 0 && <div className={`text-[12px] font-rasa ${muted}`}>—</div>}
                      {grp.rows.map(([label, count]) => (
                        <div key={label}>
                          <div className="flex items-center justify-between text-[12px] font-rasa">
                            <span className={d ? 'text-gray-300' : 'text-gray-700'}>{label}</span>
                            <span className={muted}>{count.toLocaleString()}</span>
                          </div>
                          <div className={`mt-0.5 h-1.5 rounded-full ${d ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${(count / compMax) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Client lifecycle table — at-risk surfaced first */}
      <div className={`${card} overflow-hidden`}>
        <div className={`px-4 py-3 border-b ${d ? 'border-gray-700' : 'border-gray-200'} space-y-3`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className={`text-[11px] font-bold uppercase tracking-widest font-rasa ${muted}`}>Client lifecycle — who we're winning &amp; losing</span>
            <div className="relative w-full sm:w-64">
              <Search size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client or state…"
                aria-label="Search clients by name or state"
                className={`w-full pl-8 pr-8 py-1.5 rounded-lg border text-[13px] font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
              />
              {search && (
                <button onClick={() => setSearch('')} aria-label="Clear search" className={`absolute right-2 top-1/2 -translate-y-1/2 ${muted} hover:opacity-70`}><X size={13} /></button>
              )}
            </div>
          </div>
          {/* Lifecycle chips with counts */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setLifecycleChip('')}
              aria-pressed={lifecycleChip === ''}
              className={`px-2.5 py-1 rounded-full text-[12px] font-rasa font-medium ${lifecycleChip === '' ? 'bg-blue-600 text-white' : (d ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}`}
            >
              All <span className="opacity-70">{summary.total_clients}</span>
            </button>
            {LIFECYCLE_CHIPS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setLifecycleChip(lifecycleChip === key ? '' : key)}
                aria-pressed={lifecycleChip === key}
                className={`px-2.5 py-1 rounded-full text-[12px] font-rasa font-medium ${lifecycleChip === key ? 'bg-blue-600 text-white' : (d ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}`}
              >
                {LIFECYCLE_META[key].label} <span className="opacity-70">{lc[key] || 0}</span>
              </button>
            ))}
          </div>
        </div>
        <table className="w-full">
          <thead className={d ? 'bg-gray-900/40' : 'bg-gray-50'}>
            <tr>
              <th className={th}>Client</th>
              <th className={th}>State</th>
              <th className={th}>Properties</th>
              <th className={th}>Contract</th>
              <th className={th}>Last cycle</th>
              {engagementAvailable && <th className={th}>Last login</th>}
              <th className={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={colCount} className={`${td} text-center py-8 ${muted}`}>No clients match the filter.</td></tr>
            )}
            {pageRows.map((c) => {
              const meta = LIFECYCLE_META[c.lifecycle] || LIFECYCLE_META.never;
              return (
                <tr key={c.company_id} className={`border-t ${d ? 'border-gray-700/60' : 'border-gray-100'}`}>
                  <td className={`${td} font-medium`}>{c.name || <span className={d ? 'text-gray-600' : 'text-gray-300'}>(unnamed)</span>}</td>
                  <td className={td}>{c.state || '—'}</td>
                  <td className={td}>{c.property_count}</td>
                  <td className={td}>{money(c.contract_price)}</td>
                  <td className={td}>
                    {c.latest_cycle_year || <span className={d ? 'text-gray-600' : 'text-gray-300'}>never</span>}
                    {c.years_since_last_cycle > 0 && <span className={`text-[11px] ml-1 ${muted}`}>({c.years_since_last_cycle}y)</span>}
                  </td>
                  {engagementAvailable && (
                    <td className={td}>
                      {c.last_contact_login ? (
                        <span className={daysAgo(c.last_contact_login) > 90 ? 'text-red-500' : ''}>{fmtDate(c.last_contact_login)}</span>
                      ) : (
                        <span className={d ? 'text-gray-600' : 'text-gray-300'}>never</span>
                      )}
                    </td>
                  )}
                  <td className={td}>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${d ? meta.dark : meta.bg}`}>{meta.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Pagination footer */}
        {sorted.length > PAGE_SIZE && (
          <div className={`flex items-center justify-between px-4 py-3 border-t ${d ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className={`text-[12px] font-rasa ${muted}`}>
              {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sorted.length)} of {sorted.length.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                aria-label="Previous page"
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-40`}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className={`text-[12px] font-rasa ${muted}`}>Page {safePage} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                aria-label="Next page"
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-40`}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectorDashboardTab;
